// EmailSendenDialog — Kundenmail aus dem CRM (Lead-Seite → „E-Mail").
//
// Läuft über POST /api/email/senden (E-Mail-Kanal): Unterdrückungsliste,
// Double-Opt-in-Regel für werbliche Mails und Protokoll gelten damit auch
// für den Handversand. Vorlagen kommen aus Einstellungen → E-Mail-Kanal;
// beim Auswählen werden Betreff und Text ins Formular übernommen und
// bleiben frei editierbar. Platzhalter {{vorname}}, {{name}},
// {{mitarbeiter}}, {{firma}} ersetzt der Server beim Versand.
import { useEffect, useMemo, useState } from 'react';
import { emailKanalAPI, integrationsAPI } from '@/lib/api';
import { alsText } from '@/lib/text';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Mail, Send, Loader2, Eye } from 'lucide-react';
import { toast } from 'sonner';

const PLATZHALTER = ['{{vorname}}', '{{name}}', '{{mitarbeiter}}', '{{firma}}'];

function vorschauText(text, lead, user) {
  const name = alsText(lead?.name).trim();   // Zahl/Objekt im Namensfeld darf hier nicht abstuerzen
  const ersatz = {
    '{{vorname}}': name.split(' ')[0] || '',
    '{{name}}': name,
    '{{mitarbeiter}}': user?.name || '',
    '{{firma}}': 'P1 Performance Center',
  };
  return Object.entries(ersatz).reduce((t, [k, v]) => t.split(k).join(v), text || '');
}

export default function EmailSendenDialog({ open, onClose, lead, user, onSuccess }) {
  const [an, setAn] = useState('');
  const [betreff, setBetreff] = useState('');
  const [text, setText] = useState('');
  const [typ, setTyp] = useState('transaktional');
  const [vorlagen, setVorlagen] = useState([]);
  const [vorlage, setVorlage] = useState('');
  const [verlauf, setVerlauf] = useState([]);
  const [vorschau, setVorschau] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !lead) return;
    setAn(lead.email || ''); setBetreff(''); setText(''); setTyp('transaktional');
    setVorlage(''); setVorschau(false);
    // Vorlagen aus dem E-Mail-Kanal plus die alten aus Integrationen → E-Mail
    // (gleicher Dropdown, alte Platzhalter {lead_name} versteht der Server).
    Promise.all([
      emailKanalAPI.vorlagen('p1').then((r) => r.data || []).catch(() => []),
      integrationsAPI.getEmailConfig().then((r) => (r.data?.templates || []).map((t) => ({
        slug: `alt:${t.id}`, betreff: t.subject || t.name || '', text: t.body || '', typ: 'transaktional', alt: true, name: t.name,
      }))).catch(() => []),
    ]).then(([neu, alt]) => setVorlagen([...neu, ...alt]));
    emailKanalAPI.leadNachrichten(lead.id).then((r) => setVerlauf(r.data || [])).catch(() => setVerlauf([]));
  }, [open, lead]);

  const vorlageWaehlen = (slug) => {
    setVorlage(slug);
    const v = vorlagen.find((x) => x.slug === slug);
    if (v) { setBetreff(v.betreff || ''); setText(v.text || ''); setTyp(v.typ || 'transaktional'); }
  };

  const senden = async (e) => {
    e.preventDefault();
    if (!an || !betreff || !text) return;
    setSending(true);
    try {
      const r = await emailKanalAPI.senden({
        lead_id: lead.id, an, betreff, text, typ, vorlage_slug: vorlage || null, mandant: 'p1',
      });
      toast.success(`E-Mail gesendet an ${r.data.an}`);
      onSuccess?.(); onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'E-Mail konnte nicht gesendet werden.');
    } finally { setSending(false); }
  };

  const gerendert = useMemo(() => ({
    betreff: vorschauText(betreff, lead, user), text: vorschauText(text, lead, user),
  }), [betreff, text, lead, user]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl" data-testid="email-senden-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-4 h-4 text-indigo-500" /> E-Mail an {alsText(lead?.name)}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={senden} className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">An</Label>
              <Input type="email" value={an} onChange={(e) => setAn(e.target.value)} placeholder="name@beispiel.de" required data-testid="email-an" />
            </div>
            <div>
              <Label className="text-xs">Vorlage (optional)</Label>
              <select value={vorlage} onChange={(e) => vorlageWaehlen(e.target.value)} data-testid="email-vorlage"
                      className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring">
                <option value="">— frei schreiben —</option>
                {vorlagen.map((v) => <option key={v.slug} value={v.slug}>{v.alt ? `${v.name || v.slug} (alt)` : v.slug} · {v.betreff}</option>)}
              </select>
            </div>
          </div>
          <div>
            <Label className="text-xs">Betreff</Label>
            <Input value={betreff} onChange={(e) => setBetreff(e.target.value)} required maxLength={200} data-testid="email-betreff" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label className="text-xs">Text</Label>
              <button type="button" onClick={() => setVorschau((v) => !v)} className="text-[11px] text-muted-foreground hover:text-foreground flex items-center gap-1">
                <Eye className="w-3 h-3" /> {vorschau ? 'Bearbeiten' : 'Vorschau'}
              </button>
            </div>
            {vorschau ? (
              <div className="rounded-md border border-border bg-muted/30 p-3 text-sm whitespace-pre-wrap min-h-[10rem]" data-testid="email-vorschau">
                <p className="font-medium mb-2">{gerendert.betreff}</p>
                {gerendert.text}
              </div>
            ) : (
              <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={9} required data-testid="email-text"
                        placeholder={'Hallo {{vorname}},\n\n…\n\nViele Grüße\n{{mitarbeiter}}'} />
            )}
            <p className="text-[10px] text-muted-foreground mt-1">
              Platzhalter: {PLATZHALTER.map((p) => <code key={p} className="bg-muted px-1 rounded mr-1">{p}</code>)}
            </p>
          </div>
          <label className="flex items-start gap-2 text-xs text-muted-foreground">
            <input type="checkbox" checked={typ === 'marketing'} onChange={(e) => setTyp(e.target.checked ? 'marketing' : 'transaktional')} className="mt-0.5" data-testid="email-marketing" />
            <span>Werbliche Mail (Angebot, Aktion). Geht nur an Empfänger mit bestätigtem Double-Opt-in und trägt automatisch den Abmeldelink. Kundenmails zu Termin, Vertrag oder Rückfragen bleiben ohne Haken.</span>
          </label>
          {verlauf.length > 0 && (
            <div className="border-t border-border/60 pt-2">
              <p className="text-[11px] font-medium text-muted-foreground mb-1">Bisherige Mails an diesen Kontakt</p>
              <ul className="space-y-0.5 max-h-28 overflow-y-auto" data-testid="email-verlauf">
                {verlauf.slice(0, 8).map((n) => (
                  <li key={n.id} className="flex items-center gap-2 text-[11px]">
                    <span className="text-muted-foreground whitespace-nowrap">
                      {n.erstellt_am ? new Date(n.erstellt_am).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                    </span>
                    <span className="truncate flex-1">{n.betreff}</span>
                    <Badge variant={n.status === 'gesendet' ? 'secondary' : 'destructive'} className="text-[9px] h-4 px-1" title={n.grund || ''}>{n.status}</Badge>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" type="button" onClick={onClose}>Abbrechen</Button>
            <Button type="submit" disabled={sending || !an || !betreff || !text} className="gap-1.5" data-testid="email-senden-btn">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? 'Wird gesendet …' : 'Senden'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
